/**
 * SponsorDashboard.tsx
 * Route: /sponsor/dashboard
 *
 * Features:
 * - Sponsorship summary (seats, billing, status)
 * - Add / remove seats mid-subscription
 * - View progress of each sponsored child
 * - Pool contribution view (pay-it-forward)
 * - Dedication & recognition settings
 */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  Heart, Users, BookOpen, TrendingUp, Plus, Minus,
  ChevronRight, Star, CheckCircle2, Clock, Award,
  Building2, Globe, Church, Loader2, AlertCircle,
  BarChart2, Calendar, CreditCard, Settings, ArrowRight
} from 'lucide-react'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useAdmin } from '@/hooks/useAdmin'
import { useSponsorPricing, SponsorTier, SponsorInterval } from '@/hooks/useSponsorPricing'

// Handled via supabase client

interface Sponsorship {
  id: string
  stripe_subscription_id: string | null
  quantity: number
  seats_allocated: number
  seats_used: number
  status: string
  plan_key: string | null
  billing_interval: string
  sponsor_type: string
  organisation_name: string | null
  social_handle: string | null
  allow_social_tag: boolean
  allow_logo_use: boolean
  dedication_message: string | null
  anonymous: boolean
  is_pay_it_forward: boolean
  amount_per_seat_cents: number | null
  next_billing_date: string | null
  update_preference: 'none' | 'progress' | 'full'
  created_at: string
  facility_id: string | null
  assignments: Assignment[]
}

interface Assignment {
  id: string
  assigned_name: string | null
  assigned_email: string | null
  status: string
  activated_at: string | null
  expires_at: string | null
  progress?: ProgressSnapshot | null
}

interface ProgressSnapshot {
  lessons_completed: number
  quiz_avg_score: number
  mastery_score: number
  subjects_active: number
  last_active_at: string | null
}

function SponsorTypeIcon({ type }: { type: string }) {
  const icons: Record<string, React.ReactNode> = {
    individual: <Heart size={16} />,
    business: <Building2 size={16} />,
    ngo: <Globe size={16} />,
    church: <Church size={16} />,
  }
  return <span className="text-emerald-400">{icons[type] ?? <Heart size={16} />}</span>
}

function ProgressBar({ value, max = 100, color = 'bg-emerald-500' }: { value: number; max?: number; color?: string }) {
  const pct = Math.min(100, Math.round((value / max) * 100))
  return (
    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  )
}

export default function SponsorDashboard() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isAdmin } = useAdmin()
  const { t } = useTranslation()
  const supabase = createClientComponentClient()
  const pricing = useSponsorPricing()

  const [sponsorships, setSponsorships] = useState<Sponsorship[]>([])
  const [loading, setLoading] = useState(true)
  const [seatUpdating, setSeatUpdating] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [newSeatCounts, setNewSeatCounts] = useState<Record<string, number>>({})
    // ── Sponsor-self recipient nomination (named sponsorships) ──
  const [showNominate, setShowNominate] = useState<string | null>(null)
  const [nomName, setNomName] = useState('')
  const [nomEmail, setNomEmail] = useState('')
  const [nomLoading, setNomLoading] = useState(false)
  const [nomError, setNomError] = useState<string | null>(null)

    const load = useCallback(async () => {
    if (!user) return
    setLoading(true)
    try {
      // 1. All sponsorships (1 query)
      let { data: fss } = await supabase
        .from('facility_seat_sponsorships')
        .select('*')
        .eq('sponsor_id', user.id)
        .order('created_at', { ascending: false })

      if ((!fss || fss.length === 0) && isAdmin) {
        const { data: allFss } = await supabase
          .from('facility_seat_sponsorships')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(10)
        if (allFss && allFss.length > 0) {
          fss = allFss
        }
      }

      if (!fss || fss.length === 0) { setSponsorships([]); return }

      const spIds = fss.map((s: any) => s.id)

      // 2. All assignments across sponsorships (1 query)
      const { data: allAssignments } = await supabase
        .from('sponsor_seat_assignments')
        .select('*')
        .in('sponsorship_id', spIds)
        .order('created_at', { ascending: false })

      // 3. Latest snapshot per assignment (1 query, client-side pick)
      const learnerIds = [...new Set((allAssignments ?? [])
        .map((a: any) => a.assigned_user_id).filter(Boolean))] as string[]
      const latestSnap: Record<string, any> = {}
      if (learnerIds.length > 0) {
        const { data: snaps } = await supabase
          .from('sponsor_progress_snapshots')
          .select('*')
          .in('assigned_user_id', learnerIds)
          .order('snapshot_date', { ascending: false })
        for (const s of snaps ?? []) {
          const key = `${s.sponsorship_id}:${s.assigned_user_id}`
          if (!latestSnap[key]) latestSnap[key] = s
        }
      }

      // 4. On-demand sync for active learners with no snapshot yet (mirrors
      //    the detail page) — capped at 10 to avoid invoke storms.
      const missingUsers = [...new Set((allAssignments ?? [])
        .filter((a: any) => a.status === 'active' && a.assigned_user_id && !latestSnap[`${a.sponsorship_id}:${a.assigned_user_id}`])
        .map((a: any) => a.assigned_user_id))].slice(0, 10) as string[]
      if (missingUsers.length > 0) {
        await Promise.all(missingUsers.map(uid =>
          supabase.functions.invoke('sync-sponsor-snapshots', { body: { assigned_user_id: uid } }).catch(() => {})))
        const { data: snaps2 } = await supabase
          .from('sponsor_progress_snapshots')
          .select('*')
          .in('assigned_user_id', missingUsers)
          .order('snapshot_date', { ascending: false })
        for (const s of snaps2 ?? []) {
          const key = `${s.sponsorship_id}:${s.assigned_user_id}`
          if (!latestSnap[key]) latestSnap[key] = s
        }
      }

      const enriched: Sponsorship[] = fss.map((s: any) => ({
        ...s,
        assignments: (allAssignments ?? [])
          .filter((a: any) => a.sponsorship_id === s.id)
          .map((a: any) => ({
            ...a,
            progress: a.assigned_user_id ? (latestSnap[`${s.id}:${a.assigned_user_id}`] ?? null) : null,
          })),
      }))

      setSponsorships(enriched)
      const counts: Record<string, number> = {}
      enriched.forEach((s: Sponsorship) => { counts[s.id] = s.quantity })
      setNewSeatCounts(counts)
    } catch (e) {
      console.error('Load sponsorships error:', e)
    } finally {
      setLoading(false)
    }
  }, [user, supabase])

  useEffect(() => { load() }, [load])

  async function updateSeats(s: Sponsorship) {
    const newQty = newSeatCounts[s.id]
    if (!newQty || newQty === s.quantity || !s.stripe_subscription_id) return
    setSeatUpdating(s.id)
    try {
      const { data, error } = await supabase.functions.invoke('billing', {
        body: { action: 'update_seats', stripe_subscription_id: s.stripe_subscription_id, new_quantity: newQty },
      })
      if (error || data?.error) throw new Error(error?.message || data?.error)
      await load()
    } catch (e: any) {
      alert(e.message)
    } finally {
      setSeatUpdating(null)
    }
  }

  async function cancelSponsorship(s: Sponsorship) {
    if (!s.stripe_subscription_id) return
    if (!confirm('Cancel this sponsorship? Children will lose access at the end of the current billing period.')) return
    try {
      const { data, error } = await supabase.functions.invoke('billing', {
        body: { action: 'cancel_sponsorship', stripe_subscription_id: s.stripe_subscription_id },
      })
      if (error || data?.error) throw new Error(error?.message || data?.error)
      await load()
    } catch (e: any) {
      alert(e.message)
    }
  }

    async function nominateRecipient() {
    if (!showNominate || !nomName.trim() || !nomEmail.trim()) return
    setNomLoading(true); setNomError(null)
    try {
      const { data, error } = await supabase.functions.invoke('assign-sponsor-seat', {
        body: {
          source: 'sponsor_named',
          sponsorship_id: showNominate,
          assigned_name: nomName.trim(),
          assigned_email: nomEmail.trim(),
        },
      })
      if (error || data?.error) throw new Error(error?.message || data?.error)
      setShowNominate(null); setNomName(''); setNomEmail('')
      await load()
    } catch (e: any) {
      setNomError(e.message || 'Assignment failed')
    } finally {
      setNomLoading(false)
    }
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f7f6f3]">
      <Loader2 className="animate-spin text-emerald-400" size={36} />
    </div>
  )

  if (!sponsorships.length) return (
    <div className="min-h-screen bg-[#f7f6f3] flex items-center justify-center p-6">
      <div className="text-center max-w-md space-y-6">
        <div className="w-20 h-20 rounded-3xl bg-emerald-100 flex items-center justify-center mx-auto">
          <Heart size={36} className="text-emerald-400" />
        </div>
        <h2 className="text-3xl font-black text-white">{t('sponsor.dashboard.noSponsorships')}</h2>
        <p className="text-slate-400 font-medium leading-relaxed">{t('sponsor.dashboard.noSponsorshipsDesc')}</p>
        <button onClick={() => navigate('/sponsor/give')}
          className="px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-2xl transition-all shadow-xl shadow-emerald-600/20 flex items-center gap-2 mx-auto">
          {t('sponsor.dashboard.sponsorChild')} <ArrowRight size={18} />
          <button onClick={() => navigate('/sponsor#sample')}
          className="mt-3 text-sm font-bold text-emerald-400 hover:text-emerald-300">
          Not sure yet? See a sample child's actual progress →
        </button>
        </button>
      </div>
    </div>
  )

  const isSponsorshipLive = (s: Sponsorship) => s.status === 'active' || s.status === 'canceling'
  const totalSeats = sponsorships.reduce((a, s) => a + (isSponsorshipLive(s) ? s.quantity : 0), 0)
  const usedSeats = sponsorships.reduce((a, s) => a + s.seats_used, 0)
  const totalKids = sponsorships.reduce((a, s) => a + s.assignments.filter(x => x.status === 'active').length, 0)

  return (
    <div className="min-h-screen bg-[#f7f6f3] font-sans">
      {/* Header */}
      <div className="bg-emerald-700 text-white py-10 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-emerald-300 text-xs font-black uppercase tracking-widest mb-2">{t('sponsor.dashboard.title')}</p>
          <h1 className="text-3xl font-black tracking-tight text-white">{t('sponsor.dashboard.yourImpact')}</h1>
          <p className="text-emerald-100/70 font-medium mt-1">{t('sponsor.dashboard.thankYou')}</p>

          <div className="grid grid-cols-3 gap-4 mt-8">
            {[
              { label: t('sponsor.dashboard.activeSeats'), value: totalSeats, icon: <Users size={18} /> },
              { label: t('sponsor.dashboard.childrenActive'), value: totalKids, icon: <BookOpen size={18} /> },
              { label: t('sponsor.dashboard.seatsAvailable'), value: totalSeats - usedSeats, icon: <Star size={18} /> },
            ].map(stat => (
              <div key={stat.label} className="bg-white/10 rounded-2xl p-4 border border-white/10">
                <div className="flex items-center gap-2 text-emerald-300 mb-2">{stat.icon}</div>
                <p className="text-3xl font-black text-white">{stat.value}</p>
                <p className="text-xs text-emerald-200/70 font-bold uppercase tracking-wider mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 space-y-6">

        <div className="flex items-center justify-between">
          <h2 className="text-xl font-black text-white">{t('sponsor.dashboard.yourSponsorships')}</h2>
          <button onClick={() => navigate('/sponsor/give')}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-xl transition-all">
            <Plus size={15} /> {t('sponsor.dashboard.addMoreSeats')}
          </button>
        </div>

        {sponsorships.map(s => {
          const isExpanded = expandedId === s.id
          const tier: SponsorTier = s.plan_key?.includes('bundle') ? 'bundle' : s.plan_key?.includes('elite') ? 'elite' : 'standard'
          const interval: SponsorInterval = (s.billing_interval === 'year' || s.billing_interval === 'six_months') ? s.billing_interval : 'month'
          const pricePerSeat = pricing[tier]?.[interval] ?? pricing.standard.month
          const newQty = newSeatCounts[s.id] ?? s.quantity
          const qtyChanged = newQty !== s.quantity
          const live = isSponsorshipLive(s)

          return (
            <div key={s.id} className="bg-white rounded-[1.75rem] border border-slate-200 overflow-hidden shadow-sm">
              {/* Card header */}
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-emerald-100 flex items-center justify-center">
                      <SponsorTypeIcon type={s.sponsor_type} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-black text-white">
                          {s.organisation_name || (s.anonymous ? t('sponsor.dashboard.anonymousSponsor') : t('sponsor.dashboard.personalSponsorship'))}
                        </p>
                        {s.is_pay_it_forward && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-black rounded-full uppercase tracking-wider">
                            {t('sponsor.dashboard.payItForward')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`inline-flex items-center gap-1 text-xs font-bold ${live ? 'text-emerald-400' : 'text-slate-400'}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${s.status === 'active' ? 'bg-emerald-500' : s.status === 'canceling' ? 'bg-amber-500' : 'bg-slate-300'}`} />
                          {s.status === 'active' ? t('sponsor.dashboard.active') : s.status === 'canceling' ? 'Canceling at period end' : t('sponsor.dashboard.inactive')}
                        </span>
                        <span className="text-xs text-slate-400 font-medium">
                          {t(`sponsor.dashboard.intervals.${s.billing_interval}`, t('sponsor.dashboard.intervals.month'))} · ${pricePerSeat}/seat
                        </span>
                        {s.next_billing_date && (
                          <span className="text-xs text-slate-400 font-medium flex items-center gap-1">
                            <Calendar size={11} />
                            {t('sponsor.dashboard.renews')} {new Date(s.next_billing_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <button onClick={() => setExpandedId(isExpanded ? null : s.id)}
                    className="flex items-center gap-2 text-xs font-bold text-slate-400 hover:text-emerald-400 transition-colors px-3 py-1.5 rounded-xl hover:bg-emerald-50">
                    {isExpanded ? t('sponsor.dashboard.collapse') : t('sponsor.dashboard.manage')}
                    <ChevronRight size={14} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>
                </div>

                {/* Seat bar */}
                <div className="mt-5">
                  <div className="flex justify-between text-xs font-bold text-slate-400 mb-2">
                    <span>{s.seats_used} {t('sponsor.dashboard.seatsOf')} {s.quantity} {t('sponsor.dashboard.seatsActive')}</span>
                    <span>{s.quantity - s.seats_used} {t('sponsor.dashboard.available')}</span>
                  </div>
                  <ProgressBar value={s.seats_used} max={s.quantity} />
                </div>
              </div>

              {/* Expanded: seat management + child progress */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    className="border-t border-slate-100 overflow-hidden">
                    <div className="p-6 space-y-6">

                      {/* Seat adjuster */}
                      {s.stripe_subscription_id && s.status === 'active' && s.billing_interval !== 'one-time' && (
                        <div>
                          <h3 className="text-sm font-black text-white mb-3">{t('sponsor.dashboard.adjustSeats')}</h3>
                          <div className="flex items-center gap-4 bg-slate-50 rounded-2xl p-4">
                            <button onClick={() => setNewSeatCounts(p => ({ ...p, [s.id]: Math.max(s.seats_used || 1, (p[s.id] ?? s.quantity) - 1) }))}
                              className="w-9 h-9 rounded-full border-2 border-slate-200 flex items-center justify-center hover:border-emerald-400 hover:text-emerald-400 transition-all font-black text-lg">−</button>
                            <div className="flex-1 text-center">
                              <span className="text-3xl font-black text-white">{newQty}</span>
                              <span className="text-slate-400 font-bold ml-2 text-sm">{t('sponsor.dashboard.seats')}</span>
                            </div>
                            <button onClick={() => setNewSeatCounts(p => ({ ...p, [s.id]: (p[s.id] ?? s.quantity) + 1 }))}
                              className="w-9 h-9 rounded-full border-2 border-slate-200 flex items-center justify-center hover:border-emerald-400 hover:text-emerald-400 transition-all font-black text-lg">+</button>
                            <AnimatePresence>
                              {qtyChanged && (
                                <motion.button initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                                  onClick={() => updateSeats(s)} disabled={seatUpdating === s.id}
                                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-xl transition-all flex items-center gap-2">
                                  {seatUpdating === s.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                  {t('sponsor.dashboard.update')} · ${newQty * pricePerSeat}/{s.billing_interval === 'month' ? 'mo' : s.billing_interval === 'year' ? 'yr' : '6mo'}
                                </motion.button>
                              )}
                            </AnimatePresence>
                          </div>
                          {s.seats_used > 0 && newQty < s.seats_used && (
                            <p className="text-xs text-amber-600 font-bold mt-2 flex items-center gap-1">
                              <AlertCircle size={12} /> {t('sponsor.dashboard.cannotGoBelow', { count: s.seats_used })}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Sponsor-self recipient nomination — named sponsorships with open seats */}
                      {!s.is_pay_it_forward && live && (s.quantity - s.seats_used) > 0 && (
                        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex items-center justify-between gap-4">
                          <div>
                            <p className="text-sm font-bold text-emerald-300">🎁 {s.quantity - s.seats_used} open {(s.quantity - s.seats_used) === 1 ? 'seat' : 'seats'}</p>
                            <p className="text-xs text-emerald-300 font-medium mt-0.5">{t('sponsor.know_child_who_needs_this_fund')}</p>
                          </div>
                          <button onClick={() => { setShowNominate(s.id); setNomError(null) }}
                            className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-xl transition-all flex items-center gap-2 flex-shrink-0">
                            <Plus size={14} /> Nominate
                          </button>
                        </div>
                      )}

                      {/* Child progress */}
                      {s.assignments.length > 0 && (s.update_preference === 'progress' || s.update_preference === 'full') && (
                        <div>
                          <h3 className="text-sm font-black text-white mb-3 flex items-center gap-2">
                            <TrendingUp size={14} className="text-emerald-500" /> {t('sponsor.dashboard.childProgress')}
                          </h3>
                          <div className="space-y-3">
                            {s.assignments.map(a => (
                              <div key={a.id} className="bg-slate-50 rounded-2xl p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <div>
                                    <p className="font-bold text-slate-800 text-sm">
                                      {s.update_preference === 'full'
                                        ? (a.assigned_name ?? a.assigned_email ?? t('sponsor.dashboard.pending'))
                                        : t('sponsor.dashboard.sponsoredChild')}
                                    </p>
                                    <span className={`text-xs font-bold ${a.status === 'active' ? 'text-emerald-400' : a.status === 'pending' ? 'text-amber-600' : 'text-slate-400'}`}>
                                      {a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                                    </span>
                                  </div>
                                   {a.expires_at && (
                                    <span className="text-xs text-slate-400 font-medium flex items-center gap-1">
                                      <Calendar size={11} />
                                      {t('sponsor.dashboard.expires')} {new Date(a.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </span>
                                   )}
                                   {a.status === 'active' && (a as any).assigned_user_id && (
                                    <button onClick={() => navigate(`/sponsor/child/${(a as any).assigned_user_id}`)}
                                      className="text-xs font-bold text-emerald-400 hover:text-emerald-300 flex items-center gap-1 transition-colors">
                                      {t('sponsor.dashboard.viewProgress')} <ChevronRight size={12} />
                                    </button>
                                   )}
                                </div>
                                {a.progress ? (
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('sponsor.dashboard.lessons')}</p>
                                      <p className="text-xl font-black text-white">{a.progress.lessons_completed}</p>
                                    </div>
                                    <div>
                                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('sponsor.dashboard.masteryLabel')}</p>
                                      <p className="text-xl font-black text-white">{Math.round(a.progress.mastery_score)}%</p>
                                    </div>
                                    <div className="col-span-2">
                                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">{t('sponsor.dashboard.quizAverage')}</p>
                                      <ProgressBar value={a.progress.quiz_avg_score} color={a.progress.quiz_avg_score >= 80 ? 'bg-emerald-500' : 'bg-amber-500'} />
                                      <p className="text-xs text-slate-400 font-medium mt-1">{Math.round(a.progress.quiz_avg_score)}% {t('sponsor.dashboard.across')} {a.progress.subjects_active} {t('sponsor.dashboard.subjects')}</p>
                                    </div>
                                    {a.progress.last_active_at && (
                                      <div className="col-span-2">
                                        <p className="text-[10px] text-slate-400 font-medium">
                                          {t('sponsor.dashboard.lastActive')} {new Date(a.progress.last_active_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-xs text-slate-400 font-medium italic">
                                    {a.status === 'pending' ? t('sponsor.dashboard.waitingEnrolment') : t('sponsor.dashboard.noProgressYet')}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Pay-it-forward pool note */}
                      {s.is_pay_it_forward && (s.assignments.length === 0 || s.update_preference === 'none') && (
                        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
                          <p className="text-sm font-bold text-amber-800 mb-1">🌱 {t('sponsor.dashboard.pooledContribution')}</p>
                          <p className="text-xs text-amber-700 leading-relaxed font-medium">
                            {t('sponsor.dashboard.pooledDesc', { count: s.quantity, seatWord: s.quantity === 1 ? t('sponsor.dashboard.seatIs') : t('sponsor.dashboard.seatsAre') })}
                          </p>
                        </div>
                      )}

                      {/* Cancel */}
                      {s.stripe_subscription_id && s.status === 'active' && s.billing_interval !== 'one-time' && (
                        <div className="pt-2 border-t border-slate-100">
                          <button onClick={() => cancelSponsorship(s)}
                            className="text-xs font-bold text-red-400 hover:text-red-600 transition-colors">
                            {t('sponsor.dashboard.cancelSponsorship')}
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )
        })}

                {/* ── Nominate recipient modal ── */}
        <AnimatePresence>
          {showNominate && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => { setShowNominate(null); setNomError(null) }}>
              <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                onClick={e => e.stopPropagation()}
                className="bg-white rounded-2xl border border-slate-200 p-6 max-w-md w-full shadow-2xl space-y-4">
                <div>
                  <h3 className="text-lg font-black text-white">{t('sponsor.nominate_recipient')}</h3>
                  <p className="text-xs text-slate-400 font-medium mt-1">{t('sponsor.we_ll_email_them_an_invitation')}</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">{t('sponsor.their_name')}</label>
                  <input value={nomName} onChange={e => setNomName(e.target.value)} placeholder="e.g. Maria Santos"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-emerald-400" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">{t('sponsor.their_email_or_guardian')}</label>
                  <input type="email" value={nomEmail} onChange={e => setNomEmail(e.target.value)} placeholder="them@example.com"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-emerald-400" />
                </div>
                {nomError && (
                  <p className="text-xs font-bold text-red-500 bg-red-50 border border-red-100 rounded-lg p-3">{nomError}</p>
                )}
                <div className="flex gap-2 pt-1">
                  <button onClick={() => { setShowNominate(null); setNomError(null) }}
                    className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-400 hover:text-white">Cancel</button>
                  <button onClick={nominateRecipient} disabled={nomLoading || !nomName.trim() || !nomEmail.trim()}
                    className="flex-1 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-bold flex items-center justify-center gap-2">
                    {nomLoading ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Send Invitation
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Dedication / recognition display */}
        {sponsorships.some(s => s.dedication_message) && (
          <div className="bg-white rounded-[1.75rem] p-6 border border-slate-200 shadow-sm">
            <h3 className="font-black text-white mb-4">{t('sponsor.dashboard.yourDedications')}</h3>
            {sponsorships.filter(s => s.dedication_message).map(s => (
              <div key={s.id} className="border-l-4 border-emerald-400 pl-4 py-1">
                <p className="text-white font-medium italic">"{s.dedication_message}"</p>
                <p className="text-xs text-slate-400 font-bold mt-1 uppercase tracking-wider">
                  {s.organisation_name ?? t('sponsor.dashboard.personalSponsorship')}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// Tiny inline check icon
function Check({ size, className, strokeWidth }: { size?: number; className?: string; strokeWidth?: number }) {
  return (
    <svg width={size ?? 16} height={size ?? 16} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={strokeWidth ?? 2} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}
